import { ColDef, GridApi, GridState } from 'ag-grid-enterprise';
import { pick } from 'lodash';

const GRID_COLUMN_STATE_KEY = 'gridColumnState';

export function persistColumnState(
  userId: string | undefined,
  gridStateKey: string | undefined,
  gridApi: GridApi | null,
  tableId: string | undefined
): void {
  if (userId && gridStateKey && gridApi && tableId) {
    saveToStorage(userId, gridStateKey, tableId, gridApi.getState());
  }
}

function mergePreservingOrder(a: string[], b: string[]): string[] {
  const result: string[] = [];
  let aIndex = 0;

  for (const item of b) {
    // Move through a until we find the item or exhaust a
    while (aIndex < a.length && a[aIndex] !== item) {
      result.push(a[aIndex]);
      aIndex++;
    }

    // If found in a, push it
    if (a[aIndex] === item) {
      result.push(a[aIndex]);
      aIndex++;
    } else {
      // If not found, it's extra -> insert it directly
      result.push(item);
    }
  }

  // Add remaining elements from a
  while (aIndex < a.length) {
    result.push(a[aIndex]);
    aIndex++;
  }

  return result;
}

export function retrieveColumnState(
  userId: string | undefined,
  gridStateKey: string | undefined,
  tableId: string | undefined,
  columnDefs: ColDef[]
): GridState | null {
  if (userId && gridStateKey && tableId) {
    const key = getStorageKey(userId, gridStateKey, tableId);
    const storedState = sessionStorage.getItem(key) ?? localStorage.getItem(key);
    if (!storedState) return null;
    let state = JSON.parse(storedState) as GridState;
    if (!columnDefs) return state;
    const initialOrderedColIds =
      columnDefs.map((colDef) => colDef.colId as string).filter(Boolean) ?? [];
    const loadedOrderedColIds = state.columnOrder?.orderedColIds ?? [];
    const initialColumns = new Set(initialOrderedColIds);
    const loadedColumns = new Set<string>(loadedOrderedColIds);
    const newColumns = initialColumns.difference(loadedColumns);
    if (!newColumns.size) return state;

    // new columns were added, merging their initial state
    state = {
      columnPinning: {
        leftColIds: [
          ...(state.columnPinning?.leftColIds ?? []),
          ...columnDefs
            .filter((colDef) => colDef.pinned === 'left' && newColumns.has(colDef.colId!))
            .map((colDef) => colDef.colId!),
        ],
        rightColIds: [
          ...(state.columnPinning?.rightColIds ?? []),
          ...columnDefs
            .filter((colDef) => colDef.pinned === 'right' && newColumns.has(colDef.colId!))
            .map((colDef) => colDef.colId!),
        ],
      },
      columnSizing: {
        columnSizingModel: [
          ...(state.columnSizing?.columnSizingModel ?? []),
          ...columnDefs
            .filter(
              (colDef) =>
                (colDef.initialWidth !== undefined || colDef.initialFlex !== undefined) &&
                newColumns.has(colDef.colId!)
            )
            .map((colDef) => ({
              colId: colDef.colId!,
              width: colDef.initialWidth,
              flex: colDef.initialFlex,
            })),
        ],
      },
      columnOrder: {
        orderedColIds: mergePreservingOrder(initialOrderedColIds, loadedOrderedColIds),
      },
      columnVisibility: {
        hiddenColIds: [
          ...(state.columnVisibility?.hiddenColIds ?? []),
          ...columnDefs
            .filter((colDef) => colDef.initialHide && newColumns.has(colDef.colId!))
            .map((colDef) => colDef.colId!),
        ],
      },
      sort: state.sort,
    };
    return state;
  }
  return null;
}

function saveToStorage(
  userId: string,
  gridStateKey: string,
  tableId: string,
  state: GridState
): void {
  const key = getStorageKey(userId, gridStateKey, tableId);
  const value = JSON.stringify(
    // we pick because we don't want to save things like selected items, and possibly other things
    pick(state, ['columnPinning', 'columnSizing', 'columnOrder', 'columnVisibility', 'sort'])
  );
  localStorage.setItem(key, value);
  sessionStorage.setItem(key, value);
}

function getStorageKey(userId: string, gridStateKey: string, tableId: string): string {
  return `${userId}--${GRID_COLUMN_STATE_KEY}--${gridStateKey}--${tableId}`;
}
